import { applyAbandonedCart } from 'actions/CheckoutActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import { checkoutTotalsView } from 'checkout/selectors/payment/checkout'
import { useAppDispatch } from 'hooks/reduxHooks'
import noop from 'lib/noop'
import moment from 'moment'
import React, { useCallback } from 'react'
import { connect } from 'react-redux'
import getCheckInOutText from '../../utils/getCheckInOutText'
import { getBnblOccupancyText, getOccupancyText } from '../../utils/getOccupancyText'
import ParameterizedStickyCard from '../ParameterizedStickyCard'
import { isCruiseV1 } from '../../../Common/StickyPromptCard/useAbandonedCartSelectionLogic'
import { getDeepLinkUri } from 'checkout/lib/utils/misc/deepLinkUtils'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import { dismissAbandonedCart } from 'actions/RecommendationActions'

export type AcSupportedAccommodationOfferView = (
  App.Checkout.LEAccommodationOfferView |
  App.Checkout.BedbankAccommodationOfferView |
  App.Checkout.TourV2AccommodationOfferView
)

interface Props {
  abandonedCartOfferView: AcSupportedAccommodationOfferView;
  onClose?: () => void;
  show: boolean;
  stickyTop?: number;
  totalsView: App.WithDataStatus<App.Checkout.TotalsView>;
  checkoutUrl: string;
}

const analyticsListName = getListName('LERE', 'global', 'Sticky Abandoned Cart')
const analyticsCardType = 'sticky_abandoned_card'
const cardHeading = 'Finish your booking'

function StickyAbandonedCartCard({ abandonedCartOfferView, onClose = noop, show, stickyTop, totalsView, checkoutUrl }: Props) {
  // rendered properties
  const offerDisplayName = abandonedCartOfferView.mainLabel
  const durationText = `Total for ${abandonedCartOfferView.durationLabel}`
  const checkIn = abandonedCartOfferView.startDate ? moment(abandonedCartOfferView.startDate) : undefined
  const checkOut = abandonedCartOfferView.endDate ? moment(abandonedCartOfferView.endDate) : undefined
  const checkInOutText = getCheckInOutText(checkIn, checkOut)
  // for cruise v1, we do not display sale unit (X persons)
  const saleUnit = isCruiseV1(abandonedCartOfferView) ? undefined : abandonedCartOfferView.saleUnit
  const occupancyText = abandonedCartOfferView.reservationType === 'buy_now_book_later' ?
    getBnblOccupancyText(abandonedCartOfferView.itemViews.length, saleUnit) :
    getOccupancyText(abandonedCartOfferView.occupancy, saleUnit)
  const image = abandonedCartOfferView.image
  const { savedPercentage: discountPercent, grandTotal, creditPaymentAmount } = totalsView.data
  // grandTotal may include offset from credit, so we have to add it back
  const price = grandTotal + (creditPaymentAmount ?? 0)

  // analytics properties
  const offerId = abandonedCartOfferView.offerId
  const offerAnalyticsType = abandonedCartOfferView.offer?.analyticsType ?? abandonedCartOfferView.offerType

  // activate the AB test only if we are about to show the card
  const canActivateExperiment = show && totalsView.hasRequiredData
  const experimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.lereStickyAbandonedCartEnabled, canActivateExperiment)

  const dispatchAction = useAppDispatch()

  const handleCheckout = useCallback(() => {
    dispatchAction(applyAbandonedCart())
  }, [dispatchAction])

  const handleDismiss = useCallback(() => {
    dispatchAction(dismissAbandonedCart(offerId))
    onClose()
  }, [dispatchAction, onClose, offerId])

  if (!totalsView.hasRequiredData || !experimentEnabled) { return null }

  const cardProperties = {
    offerDisplayName,
    durationText,
    checkInOutText,
    occupancyText,
    image,
    price,
    discountPercent,
    analyticsListName,
    offerId,
    offerAnalyticsType,
    analyticsCardType,
    cardHeading,
    toUrl: checkoutUrl,
    onClick: handleCheckout,
    onClose: handleDismiss,
    show,
    stickyTop,
  }

  return (
    <ParameterizedStickyCard {...cardProperties} />
  )
}

function mapStateToProps(state: App.State) {
  return {
    totalsView: checkoutTotalsView(state),
    checkoutUrl: getDeepLinkUri(state.checkout.cart.items, false),
  }
}

export default connect(mapStateToProps)(StickyAbandonedCartCard)
